import React, { useMemo, useState, useEffect, useCallback } from "react";
import BigBox from "../../../Universals/DragBox/BigBox/bigBox.component.jsx";
import { usePlayer } from "../../../../Contexts/playerData.context.jsx";
import { useNavBar } from "../../../../Contexts/navbar.items.context.jsx";
import { getUserFromToken } from "../../../../utils/functions.ts";
import { formatEasternDisplayDate } from "../../../../utils/dateUtils.ts";
import { buildPlayerWindowTitle } from "../../../../utils/playerTitle.js";

const ACCEPT_STRING = ".pdf";
const ENDPOINT = "https://www.fbxchange.com/ReactPHP/fileSection.php";

const UploadFileModal = ({
    show,
    onClose,
    defaultMode = "player",
    activePlayer,
    onUploadSuccess,
}) => {
    const [uploadMode, setUploadMode] = useState(defaultMode === "team" ? "team" : "player");
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileDesc, setFileDesc] = useState("");
    const [isPrivate, setIsPrivate] = useState("No");
    const [uploading, setUploading] = useState(false);
    const [statusMsg, setStatusMsg] = useState("");

    useEffect(() => {
        if (show) {
            setUploadMode(defaultMode === "team" ? "team" : "player");
            setSelectedFile(null);
            setFileDesc("");
            setIsPrivate("No");
            setUploading(false);
            setStatusMsg("");
        }
    }, [show, defaultMode]);

    if (!show) return null;

    const handleUpload = async () => {
        try {
            setStatusMsg("");

            if (!selectedFile) {
                setStatusMsg("Please choose a file.");
                return;
            }

            if (!fileDesc.trim()) {
                setStatusMsg("Please enter a Description before uploading.");
                return;
            }

            if (uploadMode === "player" && !activePlayer?.FBXID) {
                setStatusMsg("Please select a player first.");
                return;
            }

            const user = await getUserFromToken();
            if (!user) {
                setStatusMsg("Unable to read user token.");
                return;
            }

            setUploading(true);

            const formData = new FormData();
            formData.append("scriptName", "uploadFile");
            formData.append("displayName", user.displayName ?? "");
            formData.append("scout", user.scout ?? "");
            formData.append("team", user.team ?? "");
            formData.append("fileMode", uploadMode);
            formData.append("FBXID", uploadMode === "player" ? (activePlayer?.FBXID ?? "") : "");
            formData.append("fileDesc", fileDesc);
            formData.append("isPrivate", uploadMode === "team" ? "No" : isPrivate);
            formData.append("uploadFile", selectedFile);

            const res = await fetch(ENDPOINT, {
                method: "POST",
                body: formData,
            });

            const data = await res.json();

            if (data?.success) {
                setStatusMsg("Upload successful.");
                if (typeof onUploadSuccess === "function") {
                    await onUploadSuccess(data);
                }
                setTimeout(() => {
                    onClose();
                }, 500);
            } else {
                setStatusMsg(data?.error || "Upload failed.");
            }
        } catch (error) {
            console.error("Upload error:", error);
            setStatusMsg("Upload failed.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <>
            <div
                className="position-fixed top-0 start-0 w-100 h-100"
                style={{ background: "rgba(0,0,0,0.45)", zIndex: 5000 }}
                onClick={onClose}
            />
            <div
                className="position-fixed top-50 start-50 translate-middle bg-white rounded shadow-lg"
                style={{ width: "700px", maxWidth: "95vw", zIndex: 5001 }}
            >
                <div className="border-bottom px-3 py-2 bg-light d-flex justify-content-between align-items-center">
                    <div className="fw-bold text-uppercase">Upload File</div>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>

                <div className="p-3">
                    <div className="row g-2 mb-3">
                        <div className="col-24">
                            <button
                                type="button"
                                className={`btn btn-sm w-100 ${uploadMode === "player" ? "btn-navy" : "btn-outline-navy"}`}
                                onClick={() => setUploadMode("player")}
                            >
                                Player File
                            </button>
                        </div>
                        <div className="col-24">
                            <button
                                type="button"
                                className={`btn btn-sm w-100 ${uploadMode === "team" ? "btn-navy" : "btn-outline-navy"}`}
                                onClick={() => setUploadMode("team")}
                            >
                                Team File
                            </button>
                        </div>
                    </div>

                    {uploadMode === "player" && (
                        <div className="alert alert-secondary py-2 mb-3">
                            <strong>Player:</strong>{" "}
                            {activePlayer?.last || activePlayer?.first
                                ? `${activePlayer?.last ?? ""}, ${activePlayer?.first ?? ""}`
                                : "No player selected"}{" "}
                            {activePlayer?.FBXID ? `(${activePlayer.FBXID})` : ""}
                        </div>
                    )}

                    <div className="mb-3">
                        <label className="form-label fw-bold">Choose File</label>
                        <input
                            type="file"
                            className="form-control"
                            accept={ACCEPT_STRING}
                            onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                        />
                        <div className="form-text">
                            Allowed: PDF
                        </div>
                    </div>

                    <label className="form-label fw-bold">
                        Description <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        value={fileDesc}
                        onChange={(e) => setFileDesc(e.target.value)}
                        placeholder="Required description"
                    />
                    <div className="form-text">
                        This is the name that will appear in the file list.
                    </div>

                    {uploadMode === "player" && (
                        <div className="mb-3">
                            <label className="form-label fw-bold">Private</label>

                            <div className="d-flex gap-2">
                                <button
                                    type="button"
                                    className={`btn ${isPrivate === "No" ? "btn-navy" : "btn-outline-navy"}`}
                                    onClick={() => setIsPrivate("No")}
                                >
                                    No
                                </button>

                                <button
                                    type="button"
                                    className={`btn ${isPrivate === "Yes" ? "btn-navy" : "btn-outline-navy"}`}
                                    onClick={() => setIsPrivate("Yes")}
                                >
                                    Yes
                                </button>
                            </div>
                        </div>
                    )}

                    {statusMsg && (
                        <div className="alert alert-info py-2 mb-3">
                            {statusMsg}
                        </div>
                    )}

                    <div className="d-flex justify-content-end gap-2">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={onClose}
                            disabled={uploading}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleUpload}
                            disabled={uploading || !selectedFile || !fileDesc.trim()}
                        >
                            {uploading ? "Uploading..." : "Upload File"}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default function Files({ onClose = () => { } }) {
    const { updateActiveSideButton } = useNavBar();

    const letGoToNotes = () => {
        updateActiveSideButton("notes");
    };

    const letGoToReports = () => {
        updateActiveSideButton("reports1");
    };

    const playerCtx = usePlayer() || {};
    const {
        activePlayer,
        playerData,
        playerID,
        setActivePlayer,
        setPlayerID,
        setPlayerData,
        updatePlayerData,
    } = playerCtx;

    const [currentUser, setCurrentUser] = useState(null);
    const [selectedMode, setSelectedMode] = useState("player");
    const [selectedFile, setSelectedFile] = useState(null);
    const [playerFilesData, setPlayerFilesData] = useState([]);
    const [teamFilesData, setTeamFilesData] = useState([]);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [showUpload, setShowUpload] = useState(false);
    const [imageFailed, setImageFailed] = useState(false);
    const [actionMsg, setActionMsg] = useState("");
    const [processingAction, setProcessingAction] = useState(false);
    const filesTitle = buildPlayerWindowTitle(activePlayer, "Files");

    useEffect(() => {
        let isMounted = true;

        const loadUser = async () => {
            const user = await getUserFromToken();
            if (isMounted) {
                setCurrentUser(user || null);
            }
        };

        loadUser();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        setSelectedMode("player");
        setSelectedFile(null);
    }, [activePlayer?.FBXID, playerData]);

    const safePlayerData = useMemo(() => {
        return Array.isArray(playerData) ? playerData : [];
    }, [playerData]);

    const visibleFiles = useMemo(() => {
        return selectedMode === "team" ? teamFilesData : playerFilesData;
    }, [selectedMode, teamFilesData, playerFilesData]);

    const safeVisibleFiles = useMemo(() => {
        return Array.isArray(visibleFiles) ? visibleFiles : [];
    }, [visibleFiles]);

    const currentScoutId = useMemo(() => {
        return Number(currentUser?.scout ?? 0);
    }, [currentUser]);

    const selectedFileOwnerId = useMemo(() => {
        return Number(selectedFile?.fileByID ?? 0);
    }, [selectedFile]);

    const isMine = useMemo(() => {
        return !!selectedFile && currentScoutId > 0 && selectedFileOwnerId === currentScoutId;
    }, [selectedFile, currentScoutId, selectedFileOwnerId]);

    const isTeamFileSelected = useMemo(() => {
        return String(selectedFile?.isTeamFile ?? "No").toUpperCase() === "YES";
    }, [selectedFile]);

    const canTogglePrivate = useMemo(() => {
        return !!selectedFile && isMine && !isTeamFileSelected;
    }, [selectedFile, isMine, isTeamFileSelected]);

    const canDelete = useMemo(() => {
        return !!selectedFile && isMine;
    }, [selectedFile, isMine]);

    const canViewSelectedFile = useMemo(() => {
        if (!selectedFile) return false;

        const isPrivateFile = String(selectedFile?.isPrivate ?? "No").toUpperCase() === "YES";
        if (!isPrivateFile) return true;

        return Number(selectedFile?.fileByID ?? 0) === currentScoutId;
    }, [selectedFile, currentScoutId]);

    const formatFileDate = (rawDate) => {
        return formatEasternDisplayDate(rawDate);
    };

    const cleanFormatValue = (value) => {
        let cleanFormat = value ?? "-";

        if (typeof cleanFormat === "string") {
            const upper = cleanFormat.toUpperCase();

            if (upper.includes("PDF")) cleanFormat = "PDF";
            else if (upper.includes("CSV")) cleanFormat = "CSV";
            else if (upper.includes("TEXT") || upper.includes("TXT")) cleanFormat = "TXT";
            else if (
                upper.includes("IMAGE") ||
                upper.includes("JPG") ||
                upper.includes("JPEG") ||
                upper.includes("PNG") ||
                upper.includes("GIF") ||
                upper.includes("WEBP")
            ) cleanFormat = "IMG";
            else if (upper.includes("/")) cleanFormat = upper.split("/").pop();
            else cleanFormat = upper;
        }

        return cleanFormat;
    };

    const cleanFileArray = (arr) => {
        if (!Array.isArray(arr)) return [];

        return arr.map((f) => ({
            ...f,
            format: cleanFormatValue(f.format ?? f.fileType ?? "-"),
            fileDateFormatted: formatFileDate(f.uploadDate ?? f.fileDate),
        }));
    };

    const applyRNFUpdateToPlayerData = useCallback((rnfUpdate) => {
        if (!rnfUpdate?.FBXID) return;

        const targetFBXID = String(rnfUpdate.FBXID);
        const newPdf = Number(rnfUpdate.pdf ?? 0);

        const patchArray = (prev) => {
            if (!Array.isArray(prev)) return prev;

            return prev.map((p) => {
                if (String(p?.FBXID) !== targetFBXID) return p;

                return {
                    ...p,
                    pdf: newPdf,
                };
            });
        };

        if (typeof setPlayerData === "function") {
            setPlayerData((prev) => patchArray(prev));
        } else if (typeof updatePlayerData === "function") {
            updatePlayerData(patchArray(Array.isArray(playerData) ? playerData : []));
        }

        if (typeof setActivePlayer === "function" && String(activePlayer?.FBXID ?? "") === targetFBXID) {
            setActivePlayer((prev) => {
                if (!prev || String(prev?.FBXID ?? "") !== targetFBXID) return prev;
                return {
                    ...prev,
                    pdf: newPdf,
                };
            });
        }
    }, [setPlayerData, updatePlayerData, playerData, activePlayer, setActivePlayer]);

    const loadFiles = useCallback(async (playerIn = null) => {
        try {
            setLoadingFiles(true);
            setActionMsg("");

            const user = await getUserFromToken();
            if (!user) {
                setCurrentUser(null);
                setPlayerFilesData([]);
                setTeamFilesData([]);
                setSelectedFile(null);
                setLoadingFiles(false);
                return;
            }

            setCurrentUser(user);

            const res = await fetch(ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    scriptName: "getFiles",
                    scout: user.scout,
                    team: user.team,
                    FBXID: playerIn?.FBXID ?? activePlayer?.FBXID ?? "",
                }),
            });

            const data = await res.json();

            if (data?.success) {
                const cleanedPlayerFiles = cleanFileArray(data.playerFiles);
                const cleanedTeamFiles = cleanFileArray(data.teamFiles);

                setPlayerFilesData(cleanedPlayerFiles);
                setTeamFilesData(cleanedTeamFiles);

                const targetArray = selectedMode === "team" ? cleanedTeamFiles : cleanedPlayerFiles;

                if (selectedFile?.id) {
                    const found = targetArray.find((f) => String(f.id) === String(selectedFile.id));
                    setSelectedFile(found || (targetArray.length > 0 ? targetArray[0] : null));
                } else {
                    setSelectedFile(targetArray.length > 0 ? targetArray[0] : null);
                }
            } else {
                setPlayerFilesData([]);
                setTeamFilesData([]);
                setSelectedFile(null);
                setActionMsg(data?.error || "Unable to load files.");
            }
        } catch (error) {
            console.error("Error loading files:", error);
            setPlayerFilesData([]);
            setTeamFilesData([]);
            setSelectedFile(null);
            setActionMsg("Unable to load files.");
        } finally {
            setLoadingFiles(false);
        }
    }, [activePlayer, selectedFile?.id, selectedMode]);

    useEffect(() => {
        loadFiles(activePlayer);
    }, [activePlayer, loadFiles]);

    useEffect(() => {
        if (selectedMode === "team") {
            const found = teamFilesData.find((f) => String(f.id) === String(selectedFile?.id));
            setSelectedFile(found || (teamFilesData.length > 0 ? teamFilesData[0] : null));
        } else {
            const found = playerFilesData.find((f) => String(f.id) === String(selectedFile?.id));
            setSelectedFile(found || (playerFilesData.length > 0 ? playerFilesData[0] : null));
        }
    }, [selectedMode, teamFilesData, playerFilesData]);

    useEffect(() => {
        setImageFailed(false);
    }, [selectedFile]);

    const handleCloseFiles = () => {
        updateActiveSideButton(null);
        if (typeof onClose === "function") onClose();
    };

    const handleOpenUpload = () => {
        setShowUpload(true);
    };

    const handlePlayerClick = (player) => {
        if (!player) return;

        if (typeof setActivePlayer === "function") {
            setActivePlayer(player);
        }

        if (typeof setPlayerID === "function") {
            setPlayerID(player.FBXID);
        }

        setSelectedMode("player");
    };

    const handleFileClick = (fileRow) => {
        setSelectedFile(fileRow);
        setActionMsg("");
    };

    const handleTogglePrivate = async () => {
        if (!selectedFile || !canTogglePrivate || processingAction) return;

        const newValue = String(selectedFile?.isPrivate ?? "No").toUpperCase() === "YES" ? "No" : "Yes";

        try {
            setProcessingAction(true);
            setActionMsg("");

            const res = await fetch(ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    scriptName: "setFilePrivate",
                    fileID: selectedFile.id,
                    scout: currentUser?.scout ?? "",
                    isPrivate: newValue,
                }),
            });

            const data = await res.json();

            if (data?.success) {
                setActionMsg(`Private set to ${newValue}.`);
                await loadFiles(activePlayer);
            } else {
                setActionMsg(data?.error || "Unable to update private status.");
            }
        } catch (error) {
            console.error("Private update error:", error);
            setActionMsg("Unable to update private status.");
        } finally {
            setProcessingAction(false);
        }
    };

    const handleDeleteFile = async () => {
        if (!selectedFile || !canDelete || processingAction) return;

        const confirmDelete = window.confirm(
            `Delete this file?\n\n${selectedFile?.fileDesc ?? selectedFile?.fileName ?? "Selected file"}`
        );

        if (!confirmDelete) return;

        try {
            setProcessingAction(true);
            setActionMsg("");

            const res = await fetch(ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    scriptName: "deleteFile",
                    fileID: selectedFile.id,
                    scout: currentUser?.scout ?? "",
                }),
            });

            const data = await res.json();

            if (data?.success) {
                if (data?.rnfUpdate) {
                    applyRNFUpdateToPlayerData(data.rnfUpdate);
                }
                setActionMsg("File deleted.");
                setSelectedFile(null);
                await loadFiles(activePlayer);
            } else {
                setActionMsg(data?.error || "Unable to delete file.");
            }
        } catch (error) {
            console.error("Delete error:", error);
            setActionMsg("Unable to delete file.");
        } finally {
            setProcessingAction(false);
        }
    };

    const getFileExtension = (url = "") => {
        const clean = String(url).split("?")[0].toLowerCase();
        const parts = clean.split(".");
        return parts.length > 1 ? parts.pop() : "";
    };

    const renderFilePreview = () => {
        if (!selectedFile) {
            return (
                <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted">
                    Select a file on the left.
                </div>
            );
        }

        const isPrivateSelected = String(selectedFile?.isPrivate ?? "No").toUpperCase() === "YES";

        if (isPrivateSelected && !canViewSelectedFile) {
            return (
                <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center text-danger p-4">
                    <div className="fw-bold mb-2">Private File</div>
                    <div>You do not have permission to view this file.</div>
                </div>
            );
        }

        const format = String(selectedFile?.format ?? "").toUpperCase();
        const fileType = String(selectedFile?.fileType ?? "").toUpperCase();
        const fileUrl = selectedFile?.fileUrl ?? "";
        const ext = getFileExtension(fileUrl);

        const isPdf =
            format === "PDF" ||
            fileType.includes("PDF") ||
            ext === "pdf";

        const isImage =
            format === "IMG" ||
            fileType.includes("IMAGE") ||
            ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);

        const isTextLike =
            format === "TXT" ||
            format === "CSV" ||
            fileType.includes("TEXT") ||
            fileType.includes("CSV") ||
            ext === "txt" ||
            ext === "csv";

        if (!fileUrl) {
            return (
                <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center p-4 text-muted">
                    <div className="fw-bold mb-2">{selectedFile.fileDesc ?? selectedFile.fileName ?? "-"}</div>
                    <div className="small mb-2">
                        {selectedFile.fileDateFormatted ?? "-"} | {selectedFile.format ?? "-"}
                    </div>
                    <div>No preview URL available.</div>
                </div>
            );
        }

        if (isPdf) {
            return (
                <iframe
                    src={fileUrl}
                    title={selectedFile.fileName ?? "PDF Preview"}
                    style={{ width: "100%", height: "100%", border: "none" }}
                />
            );
        }

        if (isImage) {
            if (imageFailed) {
                return (
                    <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center p-4">
                        <div className="fw-bold mb-2">{selectedFile.fileDesc ?? selectedFile.fileName ?? "-"}</div>
                        <div className="small text-muted mb-2">
                            Image failed to load.
                        </div>
                        <div className="small mb-3 text-break text-center px-3">
                            {fileUrl}
                        </div>
                        <div className="d-flex gap-2">
                            <a
                                href={fileUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-sm btn-primary"
                            >
                                Open Image
                            </a>
                            <a
                                href={fileUrl}
                                download
                                className="btn btn-sm btn-outline-secondary"
                            >
                                Download
                            </a>
                        </div>
                    </div>
                );
            }

            return (
                <div className="w-100 h-100 d-flex align-items-center justify-content-center p-3">
                    <img
                        src={fileUrl}
                        alt={selectedFile.fileName ?? "Preview"}
                        onError={() => setImageFailed(true)}
                        style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                        }}
                    />
                </div>
            );
        }

        if (isTextLike) {
            return (
                <iframe
                    src={fileUrl}
                    title={selectedFile.fileName ?? "Text Preview"}
                    style={{ width: "100%", height: "100%", border: "none" }}
                />
            );
        }

        return (
            <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center p-4">
                <div className="fw-bold mb-2">{selectedFile.fileDesc ?? selectedFile.fileName ?? "-"}</div>
                <div className="small text-muted mb-2">
                    {selectedFile.fileDateFormatted ?? "-"} | {selectedFile.format ?? selectedFile.fileType ?? "-"}
                </div>
                <div className="small text-muted mb-2">
                    Preview not available for this file type.
                </div>
                <div className="small mb-3 text-break text-center px-3">
                    {fileUrl}
                </div>

                <div className="d-flex gap-2">
                    <a
                        href={fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm btn-primary"
                    >
                        Open File
                    </a>

                    <a
                        href={fileUrl}
                        download
                        className="btn btn-sm btn-outline-secondary"
                    >
                        Download
                    </a>
                </div>
            </div>
        );
    };

    const renderFilesTable = (heightStyle = {}) => (
        <div
            className="border rounded d-flex flex-column"
            style={{ minHeight: 0, overflow: "hidden", ...heightStyle }}
        >
            <div
                className="d-flex gap-2 bg-navy fs-7"
                style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    background: "var(--navy, #001f3f)",
                    height: "35px",
                    alignItems: "center",
                }}
            >
                <div className="flex-grow-1 text-lemon ps-2">File Name</div>
                <div className="text-center col-8 text-lemon">By</div>
                <div className="text-center col-8 text-lemon">Date</div>
                <div className="text-center col-6 text-lemon">Format</div>
            </div>

            <div style={{ overflowY: "auto", minHeight: 0, flex: 1 }}>
                {loadingFiles ? (
                    <div className="p-2 fs-7 text-muted">
                        Loading files...
                    </div>
                ) : safeVisibleFiles.length === 0 ? (
                    <div className="p-2 fs-7 text-muted">
                        No files loaded.
                    </div>
                ) : (
                    safeVisibleFiles.map((f, i) => {
                        const isPrivateRow = String(f?.isPrivate ?? "No").toUpperCase() === "YES";
                        const isSelectedRow = selectedFile?.id === f?.id;

                        return (
                            <div
                                key={f.id ?? i}
                                className={`d-flex fs-9 gap-2 py-1 align-items-center ${isSelectedRow
                                    ? "alert-danger text-dark mb-0"
                                    : isPrivateRow
                                        ? "text-danger fw-bold"
                                        : ""
                                    }`}
                                style={{
                                    borderBottom: "1px solid #ccc",
                                    minHeight: "35px",
                                    cursor: "pointer",
                                }}
                                onClick={() => handleFileClick(f)}
                            >
                                <div
                                    className="flex-grow-1 text-truncate ps-2"
                                    title={f.fileDesc ?? f.fileName ?? "-"}
                                >
                                    {f.fileDesc ?? f.fileName ?? "-"}
                                </div>
                                <div className="text-center col-8 text-truncate" title={f.fileBy ?? "-"}>
                                    {f.fileBy ?? "-"}
                                </div>
                                <div className="text-center col-8">
                                    {f.fileDateFormatted ?? "-"}
                                </div>
                                <div className="text-center col-6">
                                    {f.format ?? f.fileType ?? "-"}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );

    return (
        <>
            <BigBox
                storageKey="FILES_BOX_POSITION"
                defaultPos={{ x: 60, y: 100 }}
                title={filesTitle}
                width={1820}
                zIndex={2050}
                closeButton={true}
                onCloseButton={handleCloseFiles}
                backgroundColor="#f5edc7ff"
                showListDropdown={true}
                onAddFunction={handleOpenUpload}
                onAddText="Upload File"
                showThirdButton={true}
                thirdButtonText="View Notes"
                thirdButtonClick={() => letGoToNotes()}
                showFourthButton={true}
                fourthButtonText="View Reports"
                fourthButtonClick={() => letGoToReports()}
            >
                <div className="h-100" style={{ minHeight: 0 }}>
                    <div className="d-flex gap-3 h-100" style={{ minHeight: 0 }}>
                        <div className="col-14 d-flex flex-column h-100" style={{ minHeight: 0 }}>
                            <div
                                className="alert-light d-flex align-items-center justify-content-center gap-2 mb-2"
                                style={{ minHeight: "35px" }}
                            >
                                <div className="col-20">
                                    <button
                                        type="button"
                                        className={`btn btn-sm w-100 ${selectedMode === "player" ? "btn-navy" : "btn-outline-navy"}`}
                                        onClick={() => setSelectedMode("player")}
                                    >
                                        Player Files
                                    </button>
                                </div>
                                <div className="col-20">
                                    <button
                                        type="button"
                                        className={`btn btn-sm w-100 ${selectedMode === "team" ? "btn-navy" : "btn-outline-navy"}`}
                                        onClick={() => setSelectedMode("team")}
                                    >
                                        Team Files
                                    </button>
                                </div>
                            </div>

                            {selectedMode === "team" ? (
                                renderFilesTable({ flex: 1 })
                            ) : (
                                <>
                                    {renderFilesTable({ flex: "0 0 305px" })}

                                    <div
                                        className="mt-2 flex-grow-1 border rounded"
                                        style={{ minHeight: 0, overflowY: "auto" }}
                                    >
                                        {!safePlayerData ? (
                                            <div className="p-2 fs-7">Loading player data...</div>
                                        ) : safePlayerData.length === 0 ? (
                                            <div className="p-2 fs-7">No players in dataset</div>
                                        ) : (
                                            <div className="w-100 fs-7">
                                                <div
                                                    className="d-flex gap-2 bg-navy fs-7"
                                                    style={{
                                                        position: "sticky",
                                                        top: 0,
                                                        zIndex: 2,
                                                        background: "var(--navy, #001f3f)",
                                                        height: "35px",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <div className="flex-grow-1 text-lemon ps-2">Name</div>
                                                    <div className="text-center col-5 text-lemon">School</div>
                                                    <div className="text-center col-5 text-lemon">CGP</div>
                                                    <div className="text-center col-5 text-lemon">Col#</div>
                                                    <div className="text-center col-5 text-lemon">Pro#</div>
                                                    <div className="text-center col-5 text-lemon">RNF</div>
                                                </div>

                                                {safePlayerData.map((p, i) => (
                                                    <div
                                                        key={`${p.FBXID ?? "NOFBX"}-${i}`}
                                                        className={`d-flex fs-9 gap-2 py-1 align-items-center ${p.FBXID === playerID ? "alert-danger text-dark mb-0" : ""}`}
                                                        style={{
                                                            borderBottom: "1px solid #ccc",
                                                            height: "35px",
                                                            cursor: "pointer",
                                                        }}
                                                        onClick={() => handlePlayerClick(p)}
                                                    >
                                                        <div
                                                            className={`flex-grow-1 text-truncate ps-2
                                                                ${p.impnip === "G" ? "globalPlayer" : ""}
                                                                ${p.impnip?.startsWith("V") ? "brownPlayer" : ""}
                                                                ${p.impnip === "N" ? "text-danger" : ""}
                                                            `}
                                                        >
                                                            {`${(p.last || "").toUpperCase()}, ${(p.first || "").toUpperCase()}`}
                                                        </div>

                                                        <div className="col-5 text-center">{p.school ?? "-"}</div>
                                                        <div className="col-5 text-center">{p.genPos ?? "-"}</div>
                                                        <div className="col-5 text-center">
                                                            {p.jersey === "-1" ? "0" : p.jersey ?? "-"}
                                                        </div>
                                                        <div className="col-5 text-center">{p.proJersey ?? "-"}</div>
                                                        <div className="col-5 text-center">
                                                            {parseInt(p.report, 10) === 0 ? "-" : p.report ?? "-"}{" "}
                                                            {parseInt(p.note, 10) === 0 ? "-" : p.note ?? "-"}{" "}
                                                            {parseInt(p.pdf, 10) === 0 ? "-" : p.pdf ?? "-"}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="w-100 h-100" style={{ minHeight: 0 }}>
                            <div className="container-fluid p-0 h-100" style={{ minHeight: 0 }}>
                                <div className="row justify-content-center h-100" style={{ minHeight: 0 }}>
                                    <div className="col-48 bg-white rounded shadow-sm p-0 d-flex flex-column h-100">
                                        <div className="border-bottom px-3 py-2 bg-light">
                                            <div className="d-flex justify-content-between align-items-start gap-3">
                                                <div className="min-w-0">
                                                    <div className="fw-bold text-uppercase text-truncate">
                                                        {selectedFile?.fileDesc ?? selectedFile?.fileName ?? "File Viewer Area"}
                                                    </div>
                                                    <div className="small text-muted">
                                                        {selectedFile
                                                            ? `${selectedFile.fileDateFormatted ?? "-"} | ${selectedFile.format ?? "-"}`
                                                            : "( Select a file to preview )"}
                                                    </div>
                                                    {selectedFile && (
                                                        <div className="small text-muted">
                                                            By: <strong>{selectedFile?.fileBy ?? "-"}</strong>
                                                            {" | "}
                                                            {String(selectedFile?.isTeamFile ?? "No").toUpperCase() === "YES" ? "Team File" : "Individual File"}
                                                            {" | "}
                                                            Private: <strong>{selectedFile?.isPrivate ?? "No"}</strong>
                                                        </div>
                                                    )}
                                                </div>

                                                {selectedFile && (
                                                    <div className="d-flex flex-wrap gap-2 justify-content-end">
                                                        {canTogglePrivate && (
                                                            <button
                                                                type="button"
                                                                className={`btn btn-sm ${String(selectedFile?.isPrivate ?? "No").toUpperCase() === "YES" ? "btn-outline-warning" : "btn-outline-secondary"}`}
                                                                onClick={handleTogglePrivate}
                                                                disabled={processingAction}
                                                                title="Only your own non-team files can be made private/public"
                                                            >
                                                                {processingAction
                                                                    ? "Saving..."
                                                                    : String(selectedFile?.isPrivate ?? "No").toUpperCase() === "YES"
                                                                        ? "Set Public"
                                                                        : "Set Private"}
                                                            </button>
                                                        )}

                                                        {canDelete && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={handleDeleteFile}
                                                                disabled={processingAction}
                                                                title="Delete your own uploaded file"
                                                            >
                                                                {processingAction ? "Working..." : "Delete File"}
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {actionMsg && (
                                                <div className="alert alert-info py-2 px-2 mt-2 mb-0 fs-7">
                                                    {actionMsg}
                                                </div>
                                            )}
                                        </div>

                                        <div
                                            className="flex-grow-1"
                                            style={{ minHeight: 0, overflow: "hidden" }}
                                        >
                                            {renderFilePreview()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </BigBox>

            <UploadFileModal
                show={showUpload}
                onClose={() => setShowUpload(false)}
                defaultMode={selectedMode}
                activePlayer={activePlayer}
                onUploadSuccess={async (data) => {
                    if (data?.rnfUpdate) {
                        applyRNFUpdateToPlayerData(data.rnfUpdate);
                    }
                    await loadFiles(activePlayer);
                }}
            />
        </>
    );
}
